<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import MissionReward from '@/components/smartico/apostatudo/items/MissionReward.vue';
import { useAuthStore } from '@/stores/auth';
import { useSmarticoBadges } from '@/stores/smartico/badges';
import { useSmarticoMissions } from '@/stores/smartico/missions';
import {
  ALBUM_CATEGORY_ID,
  ALBUM_SECTIONS,
  ALBUM_SLOTS_PER_PAGE_DESKTOP,
  ALBUM_SLOTS_PER_PAGE_MOBILE,
  getSectionByCustomId,
  type AlbumSection,
  type AlbumSectionInfo,
} from '~/utils/albumStickers';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';

const props = defineProps<{
  config: AffiliateConfig;
}>();

const { isMobile } = useLayout();

const slotsPerPage = computed(() =>
  isMobile.value ? ALBUM_SLOTS_PER_PAGE_MOBILE : ALBUM_SLOTS_PER_PAGE_DESKTOP,
);

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);
const { openLogin } = useAuth();

const badgesStore = useSmarticoBadges();
const missionsStore = useSmarticoMissions();
const { badges } = storeToRefs(badgesStore);
const { missions } = storeToRefs(missionsStore);

const albumCategoryId = computed(
  () => props.config.albumCategoryId ?? ALBUM_CATEGORY_ID,
);

// Badges do álbum (filtradas por category_id e ordenadas por position dentro da seção)
const albumBadges = computed(() =>
  badges.value
    .filter((b) => b.category_ids?.includes(albumCategoryId.value))
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
);

// Agrupa as badges por seção (custom_section_id → AlbumSection)
const badgesBySection = computed(() => {
  const map = new Map<AlbumSection, TMissionOrBadge[]>();

  for (const section of ALBUM_SECTIONS) {
    map.set(section.id, []);
  }

  for (const badge of albumBadges.value) {
    const section = getSectionByCustomId(badge.custom_section_id);

    if (section) map.get(section.id)?.push(badge);
  }

  return map;
});

// Seções com pelo menos uma badge — usado pra dock e paginação
const activeSections = computed<AlbumSectionInfo[]>(() =>
  ALBUM_SECTIONS.filter(
    (section) => (badgesBySection.value.get(section.id)?.length ?? 0) > 0,
  ),
);

// Cada seção pode ter múltiplas páginas (ALBUM_SLOTS_PER_PAGE por página)
// Estrutura linear de páginas: [{ section, pageInSection, badges }]
interface AlbumPage {
  section: AlbumSectionInfo;
  pageInSection: number;
  totalPagesInSection: number;
  badges: TMissionOrBadge[];
}

const pages = computed<AlbumPage[]>(() => {
  const result: AlbumPage[] = [];
  const perPage = slotsPerPage.value;

  for (const section of activeSections.value) {
    const sectionBadges = badgesBySection.value.get(section.id) ?? [];
    const totalPages = Math.max(1, Math.ceil(sectionBadges.length / perPage));

    for (let p = 0; p < totalPages; p++) {
      result.push({
        section,
        pageInSection: p + 1,
        totalPagesInSection: totalPages,
        badges: sectionBadges.slice(p * perPage, (p + 1) * perPage),
      });
    }
  }

  return result;
});

// Folhas individuais (meia-página) consumidas pelo flipbook. Cada spread vira
// duas folhas: a metade esquerda e a direita, na mesma ordem linear das páginas.
interface AlbumLeaf {
  key: string;
  side: 'left' | 'right';
  slots: (TMissionOrBadge | null)[];
  section: AlbumSectionInfo;
}

const leaves = computed<AlbumLeaf[]>(() => {
  const perPage = slotsPerPage.value;
  const half = Math.ceil(perPage / 2);
  const result: AlbumLeaf[] = [];

  for (const page of pages.value) {
    const padded: (TMissionOrBadge | null)[] = [];

    for (let i = 0; i < perPage; i++) padded.push(page.badges[i] ?? null);

    const base = `${page.section.id}-${page.pageInSection}`;

    result.push({
      key: `${base}-L`,
      side: 'left',
      slots: padded.slice(0, half),
      section: page.section,
    });
    result.push({
      key: `${base}-R`,
      side: 'right',
      slots: padded.slice(half),
      section: page.section,
    });
  }

  return result;
});

// Índice da folha atual (atualizado pelo evento `flip` do StPageFlip).
const flipbookRef = ref<{
  flipNext: () => void;
  flipPrev: () => void;
  flipTo: (index: number) => void;
} | null>(null);

const currentLeafIndex = ref(0);

// Spread = par de folhas; usado pra (des)habilitar as setas.
const currentSpread = computed(() => Math.floor(currentLeafIndex.value / 2));
const totalSpreads = computed(() => Math.max(1, pages.value.length));
const prevDisabled = computed(() => currentSpread.value <= 0);
const nextDisabled = computed(
  () => currentSpread.value >= totalSpreads.value - 1,
);

const currentSection = computed<AlbumSectionInfo | null>(
  () =>
    leaves.value[currentLeafIndex.value]?.section ??
    activeSections.value[0] ??
    null,
);

// Reabre o flipbook na folha em que o usuário estava (preserva entre remounts).
const initialLeafIndex = computed(() =>
  Math.min(currentLeafIndex.value, Math.max(0, leaves.value.length - 1)),
);

// Remonta o flipbook (re-inicializa o StPageFlip) quando a estrutura de folhas
// muda — troca de breakpoint, chegada das badges reais ou novas coletas.
const flipbookKey = computed(
  () => `${slotsPerPage.value}-${leaves.value.length}-${collectedCount.value}`,
);

// Contador de figurinhas coletadas
const collectedCount = computed(
  () => albumBadges.value.filter((b) => b.is_completed).length,
);

// Primeira badge (menor position) de cada seção — usada como ícone do dock
function getSectionIconBadge(
  section: AlbumSection,
): TMissionOrBadge | undefined {
  const list = badgesBySection.value.get(section) ?? [];

  return list[0];
}

function onLeafFlip(index: number) {
  currentLeafIndex.value = index;
}

function flipPage(dir: number) {
  if (dir > 0) flipbookRef.value?.flipNext();
  else flipbookRef.value?.flipPrev();
}

function goToSection(sectionId: AlbumSection) {
  const idx = leaves.value.findIndex((l) => l.section.id === sectionId);

  if (idx >= 0) flipbookRef.value?.flipTo(idx);
}

// Modal de detalhes da figurinha (substitui o antigo Showcase + abertura direta de missão).
// Auth gate: usuário não autenticado é redirecionado pro login antes de ver o detalhe.
const isDetailModalVisible = ref(false);
const selectedBadge = ref<TMissionOrBadge | null>(null);

function handleOpenDetail(badge: TMissionOrBadge) {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }

  selectedBadge.value = badge;
  isDetailModalVisible.value = true;
}

watch(isDetailModalVisible, (open) => {
  if (!open) selectedBadge.value = null;
});

// Missão aberta a partir do `?open=<id>` interceptado no detail modal.
// Empilha por cima do detail; se o id não existe no store, é no-op.
const isMissionModalVisible = ref(false);
const selectedMission = ref<TMissionOrBadge | null>(null);

async function handleOpenMissionById(missionId: number) {
  if (!missions.value.length) {
    await missionsStore.loadMissions();
  }

  const mission = missions.value.find((m) => m.id === missionId);

  if (!mission) return;

  selectedMission.value = mission;
  isMissionModalVisible.value = true;
}

watch(isMissionModalVisible, (open) => {
  if (!open) selectedMission.value = null;
});

// Quando troca desktop ↔ mobile, o número de folhas muda — manter o índice no range.
watch(leaves, (list) => {
  if (currentLeafIndex.value >= list.length) {
    currentLeafIndex.value = Math.max(0, list.length - 1);
  }
});

// Aguarda window._smartico.api ficar realmente funcional antes de buscar
// badges/missions reais. Sem isso, um login no meio da sessão chama getBadges
// cedo demais, a API retorna vazio e o fallback de mock é reaplicado.
const { waitForReady } = useSmarticoUserReady(undefined, [
  'getBadges',
  'getMissions',
]);

waitForReady(async () => {
  if (!isAuthenticated.value) return;
  await Promise.all([badgesStore.loadBadges(), missionsStore.loadMissions()]);
});

// Mock entra imediatamente pra evitar tela vazia enquanto o Smartico inicializa;
// o waitForReady acima sobrescreve com os dados reais quando o usuário estiver
// logado e a API responder.
onMounted(() => {
  if (!isAuthenticated.value) {
    badgesStore.loadMockBadges();
  }
});
</script>

<template>
  <div class="album-section-container mb-4 -mt-4 md:-mt-2 lg:mt-4 w-full">
    <img
      alt="Copa Deluxe"
      class="copa-deluxe-banner"
      draggable="false"
      :src="
        getAssetsUrlForCDN('/assets/images/perfil-oficial/copa/copa-deluxe.png')
      "
      @dragstart.prevent
    />
    <div class="album-wrapper">
      <!-- Setas laterais -->
      <button
        v-if="pages.length > 1"
        aria-label="Página anterior"
        class="nav-arrow-btn prev-arrow"
        :disabled="prevDisabled"
        @click="flipPage(-1)"
      >
        ❮
      </button>
      <button
        v-if="pages.length > 1"
        aria-label="Próxima página"
        class="nav-arrow-btn next-arrow"
        :disabled="nextDisabled"
        @click="flipPage(1)"
      >
        ❯
      </button>

      <!-- Header: logo à esquerda, pill seção + contador centralizados -->
      <div
        v-if="currentSection"
        class="album-header"
      >
        <img
          alt="Apostatudo"
          class="brand-logo"
          draggable="false"
          :src="getAssetsUrlForCDN('/assets/images/aposta-tudo.png')"
          @dragstart.prevent
        />

        <div class="header-pills">
          <div class="pill-container">
            <img
              v-if="
                currentSection.iconType === 'flag' ||
                currentSection.iconType === 'image'
              "
              :alt="currentSection.label"
              class="header-icon"
              draggable="false"
              :src="currentSection.iconValue"
              @dragstart.prevent
            />
            <img
              v-else-if="getSectionIconBadge(currentSection.id)?.image"
              :alt="currentSection.label"
              class="header-icon"
              draggable="false"
              :src="getSectionIconBadge(currentSection.id)!.image"
              @dragstart.prevent
            />
            <div
              v-else
              class="header-emoji"
              >{{ currentSection.iconValue }}</div
            >
            <span class="header-text">{{ currentSection.label }}</span>
          </div>

          <div class="pill-container counter-pill">
            <span class="counter-value">
              {{ collectedCount
              }}<span class="counter-total">/{{ albumBadges.length }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Livro virtual: arraste as bordas e a folha acompanha o cursor (StPageFlip) -->
      <div class="pages-region">
        <ClientOnly>
          <PerfilOficialAlbumFlipbook
            v-if="leaves.length"
            :key="flipbookKey"
            ref="flipbookRef"
            :initial-index="initialLeafIndex"
            :leaves="leaves"
            @flip="onLeafFlip"
            @open-detail="handleOpenDetail"
          />
        </ClientOnly>
      </div>

      <!-- Dock de navegação entre seções (Brasil / Mundo / Lendas / Seleção) -->
      <div
        v-if="activeSections.length > 1"
        class="dock-navbar"
      >
        <div
          v-for="section in activeSections"
          :key="section.id"
          class="dock-item"
          :class="{
            'active-dock': currentSection?.id === section.id,
          }"
          @click="goToSection(section.id)"
        >
          <!-- Ícone configurado da seção tem prioridade sobre a primeira badge -->
          <img
            v-if="section.iconType === 'flag' || section.iconType === 'image'"
            :alt="section.label"
            draggable="false"
            :src="section.iconValue"
            @dragstart.prevent
          />
          <img
            v-else-if="getSectionIconBadge(section.id)?.image"
            :alt="section.label"
            draggable="false"
            :src="getSectionIconBadge(section.id)!.image"
            @dragstart.prevent
          />
          <div
            v-else
            class="dock-emoji"
            >{{ section.iconValue }}</div
          >
        </div>
      </div>
    </div>

    <!-- Modal de detalhes da figurinha -->
    <PerfilOficialAlbumStickerDetailModal
      v-model="isDetailModalVisible"
      :badge="selectedBadge"
      :badges="albumBadges"
      @open-mission="handleOpenMissionById"
      @select-badge="(b) => (selectedBadge = b)"
    />

    <!-- Missão aberta a partir do `?open=<id>` do custom_data; empilhada sobre o
         detail modal (cujo overlay é z-index 100000), por isso fica acima dele -->
    <MissionReward
      v-if="selectedMission"
      v-model:modal-visible="isMissionModalVisible"
      :mission="selectedMission"
      :show-participate-button="true"
      :z-index="100001"
    />
  </div>
</template>

<style scoped>
.album-section-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.copa-deluxe-banner {
  width: 50%;
  max-width: 550px;
  height: auto;
  display: block;
  margin-top: -4.5%;
  pointer-events: none;
}

.album-section-container :deep(img) {
  -webkit-user-drag: none;
}

.album-wrapper {
  width: 100%;
  background-color: transparent;
  background-image:
    radial-gradient(
      121.1% 88.65% at 50% 0%,
      rgba(41, 41, 255, 0.89) 0%,
      rgba(41, 41, 255, 0) 55%
    ),
    linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(0, 0, 0, 0) 8%,
      rgba(0, 0, 0, 0) 92%,
      rgba(0, 0, 0, 0.85) 100%
    ),
    linear-gradient(126deg, #0f0f4f 0%, #030312 100%);
  background-size:
    100% 100%,
    100% 100%,
    100% 100%;
  border: 0.532px solid rgba(255, 255, 255, 0.08);
  border-radius: 10.631px;
  box-shadow:
    0 0 42.526px 0.532px rgba(0, 0, 0, 0.95) inset,
    0 15.947px 42.526px 0 rgba(0, 0, 0, 0.95);
  position: relative;
  overflow: hidden;
  color: #fff;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .album-wrapper::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 80px;
    transform: translateX(-50%);
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.24) 30%,
      rgba(0, 0, 0, 0.48) 50%,
      rgba(0, 0, 0, 0.24) 70%,
      rgba(0, 0, 0, 0) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  .album-wrapper::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(41, 41, 255, 0.3),
      transparent
    );
    pointer-events: none;
    z-index: 2;
  }
}

@media (max-width: 767px) {
  .album-wrapper::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 20px;
    transform: translateX(-50%);
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.12) 30%,
      rgba(0, 0, 0, 0.24) 50%,
      rgba(0, 0, 0, 0.12) 70%,
      rgba(0, 0, 0, 0) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  .album-wrapper::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background: linear-gradient(
      180deg,
      rgba(41, 41, 255, 0) 0%,
      #2929ff 50%,
      rgba(41, 41, 255, 0) 100%
    );
    opacity: 0.6;
    pointer-events: none;
    z-index: 10;
  }
}

/* Header flutua POR CIMA das páginas (absolute) pra que o fundo da página comece
   no topo do wrapper — assim o glow azul do topo fica contínuo/homogêneo entre o
   header e a área das figurinhas, sem a "faixa" dupla. */
.album-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0 15px;
  z-index: 6;
  min-height: 31px;
}

.brand-logo {
  position: absolute;
  left: 40px;
  top: 50%;
  transform: translateY(-50%);
  width: 110px;
  height: auto;
  opacity: 0.9;
  pointer-events: none;
}

.header-pills {
  display: flex;
  align-items: center;
  gap: 8px;
}

.counter-total {
  color: #888;
  font-size: 9px;
  font-weight: 100;
}

.pill-container {
  background: rgba(10, 2, 5, 0.75);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(41, 41, 255, 0.4);
  border-radius: 50px;
  padding: 3px 12px 3px 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
}

.counter-pill {
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  padding: 3px 10px;
  border-color: rgba(41, 41, 255, 0.55);
}

.header-text {
  font-size: 9px;
  font-weight: 100;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.counter-value {
  color: #ff3d1f;
  font-weight: 100;
  font-size: 9px;
}

.header-icon {
  width: 15px;
  height: 15px;
  object-fit: contain;
}

.header-emoji {
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
}

.pages-region {
  position: relative;
  width: 100%;
  padding: 0;
  z-index: 5;
}

.nav-arrow-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background: rgba(6, 6, 20, 0.9);
  border: 1px solid rgba(41, 41, 255, 0.35);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  transition: all 0.2s;
}

.nav-arrow-btn:disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.nav-arrow-btn:hover:not(:disabled) {
  background: #2929ff;
  transform: translateY(-50%) scale(1.1);
  border-color: #2929ff;
}

.prev-arrow {
  left: 20px;
}

.next-arrow {
  right: 20px;
}

.dock-navbar {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(
    to right,
    rgba(20, 20, 221, 0.7) 0%,
    rgba(20, 20, 221, 0.2) 100%
  );
  backdrop-filter: blur(9px);
  border: 1px solid rgba(41, 41, 255, 0.3);
  border-radius: 100px;
  padding: 0 32px;
  height: 40px;
  align-items: center;
  display: flex;
  gap: 32px;
  z-index: 10;
  box-shadow: 0 14px 30px rgba(20, 20, 221, 0.55);
  max-width: calc(100% - 32px);
}

.dock-item {
  opacity: 0.4;
  transition: opacity 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.dock-item img {
  width: 30px;
  height: 30px;
  object-fit: contain;
  object-position: center;
  display: block;
  transition: 0.3s;
}

.dock-emoji {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: 0.3s;
}

.dock-item.active-dock {
  opacity: 1;
}

.dock-item.active-dock .dock-emoji {
  border-color: #2929ff;
  box-shadow: 0 3px 9px rgba(41, 41, 255, 0.4);
}

@media (max-width: 767px) {
  .copa-deluxe-banner {
    width: 100%;
  }

  .album-header {
    padding: 6px 12px 12px;
    justify-content: space-between;
    gap: 8px;
  }

  .brand-logo {
    position: static;
    transform: none;
    width: clamp(80px, 28vw, 110px);
    flex: 0 0 auto;
    order: 2;
  }

  .header-pills {
    display: contents;
  }

  .header-text {
    letter-spacing: 1px;
  }

  .pill-container {
    order: 1;
  }

  .counter-pill {
    position: static;
    transform: none;
    right: auto;
    top: auto;
    order: 3;
  }

  .nav-arrow-btn {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .prev-arrow {
    left: 8px;
  }

  .next-arrow {
    right: 8px;
  }

  .dock-navbar {
    width: max-content;
    padding: 0 20px;
    gap: 20px;
    bottom: 8px;
  }
}
</style>
